import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card } from '../../ui/card';
import { Plus, Edit, Trash2, Phone } from 'lucide-react';
import { API } from '../../../lib/api';

const AgentsTab = ({ agents, getAuthHeaders, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [form, setForm] = useState({ name: '', whatsapp_number: '' });
  const [saving, setSaving] = useState(false);

  const maskWA = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 13);
    if (d.length <= 2) return `(${d}`;
    if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  };

  const resetForm = () => {
    setForm({ name: '', whatsapp_number: '' });
    setEditingAgent(null);
    setShowForm(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingAgent) {
        await axios.put(
          `${API}/agents/${editingAgent.id}`,
          { name: form.name, whatsapp_number: form.whatsapp_number.replace(/\D/g, '') },
          getAuthHeaders()
        );
        toast.success('Agente atualizado!');
      } else {
        await axios.post(
          `${API}/agents`,
          { name: form.name, whatsapp_number: form.whatsapp_number.replace(/\D/g, '') },
          getAuthHeaders()
        );
        toast.success('Agente criado!');
      }
      resetForm();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar agente');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este agente?')) return;
    try {
      await axios.delete(`${API}/agents/${id}`, getAuthHeaders());
      toast.success('Agente excluído');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800">Agentes de Atendimento ({agents.length})</h2>
        <Button
          onClick={() => { resetForm(); setShowForm(!showForm); }}
          className="bg-orange-600 hover:bg-orange-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          {showForm && !editingAgent ? 'Cancelar' : 'Novo Agente'}
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 border-orange-200 bg-orange-50/30">
          <h3 className="font-bold text-gray-800 mb-4">{editingAgent ? 'Editar Agente' : 'Novo Agente'}</h3>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Nome do Agente *</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required placeholder="Ex: Arthur, João..."
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">WhatsApp *</label>
                <Input
                  value={form.whatsapp_number}
                  onChange={(e) => setForm({ ...form, whatsapp_number: maskWA(e.target.value) })}
                  required inputMode="numeric" placeholder="(38) 99999-9999"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" onClick={resetForm} variant="outline" className="flex-1">Cancelar</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white">
                {saving ? 'Salvando...' : editingAgent ? 'Salvar' : 'Criar Agente'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {agents.length === 0 && !showForm && (
          <div className="col-span-full">
            <Card className="p-12 text-center">
              <Phone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Nenhum agente cadastrado ainda.</p>
              <p className="text-gray-400 text-sm mt-1">Crie agentes para vincular a parceiros.</p>
            </Card>
          </div>
        )}
        {agents.map((a) => (
          <Card key={a.id} className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-3">
                  <Phone className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="font-bold text-gray-800">{a.name}</h3>
                <p className="text-sm text-gray-500 mt-0.5 font-mono">+{a.whatsapp_number}</p>
              </div>
              <div className="flex gap-1.5">
                <button
                  onClick={() => {
                    setForm({
                      name: a.name,
                      whatsapp_number: `(${a.whatsapp_number.slice(2, 4)}) ${a.whatsapp_number.slice(4, 9)}-${a.whatsapp_number.slice(9)}`
                    });
                    setEditingAgent(a);
                    setShowForm(true);
                  }}
                  className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <Edit className="w-3.5 h-3.5 text-gray-600" />
                </button>
                <button
                  onClick={() => handleDelete(a.id)}
                  className="p-1.5 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-600" />
                </button>
              </div>
            </div>
            <a
              href={`https://wa.me/${a.whatsapp_number}`}
              target="_blank" rel="noreferrer"
              className="mt-3 inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium"
            >
              Abrir no WhatsApp →
            </a>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AgentsTab;
